import React from 'react';
import { 
  Calendar, 
  Filter, 
  Download, 
  Eye, 
  BarChart3,
  Settings,
  ChevronDown,
  CalendarDays
} from 'lucide-react';
import { Button } from '../ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { CashFlowFilters as CashFlowFiltersType, CashFlowViewMode, CashFlowPeriod } from '../../types/cash-flow';

interface CashFlowFiltersProps {
  filters: CashFlowFiltersType;
  onFiltersChange: (filters: CashFlowFiltersType) => void;
  availableAccounts?: Array<{ id: string; name: string }>;
  availableCategories?: Array<{ id: string; name: string }>;
  viewMode: CashFlowViewMode;
  onViewModeChange: (mode: CashFlowViewMode) => void;
  onExport?: () => void;
  loading?: boolean;
}

const PERIOD_OPTIONS: Array<{ value: CashFlowPeriod; label: string }> = [
  { value: 'today', label: 'Hoje' },
  { value: 'week', label: 'Esta semana' },
  { value: 'month', label: 'Este mês' },
  { value: 'quarter', label: 'Este trimestre' },
  { value: 'year', label: 'Este ano' },
  { value: 'custom', label: 'Período personalizado' }
];

const VISUALIZATION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'diario', label: 'Diário' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'mensal', label: 'Mensal' }
];

export function CashFlowFilters({
  filters,
  onFiltersChange,
  availableAccounts = [],
  availableCategories = [],
  viewMode,
  onViewModeChange,
  onExport,
  loading = false
}: CashFlowFiltersProps) {
  
  const handlePeriodChange = (periodType: CashFlowPeriod) => {
    const now = new Date();
    let inicio: Date, fim: Date;

    switch (periodType) {
      case 'today':
        inicio = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        fim = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        break;
      case 'week':
        const dayOfWeek = now.getDay();
        inicio = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
        fim = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (6 - dayOfWeek) + 1);
        break;
      case 'month':
        inicio = new Date(now.getFullYear(), now.getMonth(), 1);
        fim = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        break;
      case 'quarter':
        const quarterStart = Math.floor(now.getMonth() / 3) * 3;
        inicio = new Date(now.getFullYear(), quarterStart, 1);
        fim = new Date(now.getFullYear(), quarterStart + 3, 1);
        break;
      case 'year':
        inicio = new Date(now.getFullYear(), 0, 1);
        fim = new Date(now.getFullYear() + 1, 0, 1);
        break;
      default:
        return; // For custom period, don't auto-set dates
    }

    onFiltersChange({
      ...filters,
      periodo: { inicio, fim }
    });
  };

  const handleVisualizationChange = (value: string) => {
    onFiltersChange({
      ...filters,
      visualizacao: value as 'diario' | 'semanal' | 'mensal'
    });
  };

  const handleProjectionsToggle = (incluir_projecoes: boolean) => {
    onFiltersChange({
      ...filters,
      incluir_projecoes
    });
  };

  const handleAccountsChange = (accountIds: string[]) => {
    onFiltersChange({
      ...filters,
      contas_selecionadas: accountIds
    });
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const getPeriodLabel = () => {
    const { inicio, fim } = filters.periodo;
    const startStr = formatDate(inicio);
    const endStr = formatDate(fim);
    
    if (startStr === endStr) {
      return startStr;
    }
    
    return `${startStr} - ${endStr}`;
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.contas_selecionadas?.length) count++;
    if (filters.categorias_selecionadas?.length) count++;
    if (filters.incluir_projecoes) count++;
    return count;
  };

  return (
    <div className="space-y-4">
      {/* Main Filters Row */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Period Selector */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full sm:w-auto justify-start">
              <Calendar className="h-4 w-4 mr-2" />
              {getPeriodLabel()}
              <ChevronDown className="h-4 w-4 ml-auto" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-3">Período</h4>
                <div className="grid grid-cols-2 gap-2">
                  {PERIOD_OPTIONS.map(option => (
                    <Button
                      key={option.value}
                      variant="outline"
                      size="sm"
                      onClick={() => handlePeriodChange(option.value)}
                      className="justify-start"
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Custom Date Range - TODO: Implement date picker */}
              <div className="pt-3 border-t">
                <Label className="text-sm font-medium">Período Personalizado</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Seleção de datas personalizada em desenvolvimento
                </p>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Visualization Type */}
        <Select value={filters.visualizacao} onValueChange={handleVisualizationChange}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Visualização" />
          </SelectTrigger>
          <SelectContent>
            {VISUALIZATION_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* View Mode Toggle */}
        <div className="flex rounded-md border">
          <Button
            variant={viewMode === 'chart' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('chart')}
            className="rounded-r-none"
          >
            <BarChart3 className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'table' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('table')}
            className="rounded-none border-x"
          >
            <CalendarDays className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'both' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewModeChange('both')}
            className="rounded-l-none"
          >
            <Eye className="h-4 w-4" />
          </Button>
        </div>

        {/* Advanced Filters */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Filtros
              {getActiveFiltersCount() > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 text-xs">
                  {getActiveFiltersCount()}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel>Filtros Avançados</DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {/* Projections Toggle */}
            <div className="px-2 py-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="projections" className="text-sm font-medium">
                  Incluir Projeções
                </Label>
                <Switch
                  id="projections"
                  checked={filters.incluir_projecoes}
                  onCheckedChange={handleProjectionsToggle}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Exibir projeções futuras baseadas no histórico
              </p>
            </div>
            
            <DropdownMenuSeparator />
            
            {/* Account Filter */}
            {availableAccounts.length > 0 && (
              <>
                <DropdownMenuLabel>Contas</DropdownMenuLabel>
                <div className="max-h-32 overflow-y-auto px-2 pb-2">
                  {availableAccounts.map(account => (
                    <div key={account.id} className="flex items-center space-x-2 py-1">
                      <input
                        type="checkbox"
                        id={`account-${account.id}`}
                        checked={filters.contas_selecionadas?.includes(account.id) || false}
                        onChange={(e) => {
                          const current = filters.contas_selecionadas || [];
                          if (e.target.checked) {
                            handleAccountsChange([...current, account.id]);
                          } else {
                            handleAccountsChange(current.filter(id => id !== account.id));
                          }
                        }}
                        className="rounded border-border text-primary focus-internal"
                      />
                      <Label 
                        htmlFor={`account-${account.id}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {account.name}
                      </Label>
                    </div>
                  ))}
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            
            {/* Category Filter */}
            {availableCategories.length > 0 && (
              <>
                <DropdownMenuLabel>Categorias</DropdownMenuLabel>
                <div className="max-h-32 overflow-y-auto px-2 pb-2">
                  {availableCategories.map(category => (
                    <div key={category.id} className="flex items-center space-x-2 py-1">
                      <input
                        type="checkbox"
                        id={`category-${category.id}`}
                        checked={filters.categorias_selecionadas?.includes(category.id) || false}
                        onChange={(e) => {
                          const current = filters.categorias_selecionadas || [];
                          if (e.target.checked) {
                            onFiltersChange({
                              ...filters,
                              categorias_selecionadas: [...current, category.id]
                            });
                          } else {
                            onFiltersChange({
                              ...filters,
                              categorias_selecionadas: current.filter(id => id !== category.id)
                            });
                          }
                        }}
                        className="rounded border-border text-primary focus-internal"
                      />
                      <Label 
                        htmlFor={`category-${category.id}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {category.name}
                      </Label>
                    </div>
                  ))}
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            
            {onExport && (
              <DropdownMenuItem onClick={onExport}>
                <Download className="h-4 w-4 mr-2" />
                Exportar dados filtrados
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Settings */}
        <Button variant="ghost" size="sm">
          <Settings className="h-4 w-4" />
        </Button>
      </div>

      {/* Active Filters Display */}
      {getActiveFiltersCount() > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Filtros ativos:</span>
          
          {filters.incluir_projecoes && (
            <Badge variant="secondary" className="text-xs">
              Com projeções
            </Badge>
          )}
          
          {filters.contas_selecionadas?.length && (
            <Badge variant="secondary" className="text-xs">
              {filters.contas_selecionadas.length} conta(s)
            </Badge>
          )}
          
          {filters.categorias_selecionadas?.length && (
            <Badge variant="secondary" className="text-xs">
              {filters.categorias_selecionadas.length} categoria(s)
            </Badge>
          )}
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onFiltersChange({
              ...filters,
              incluir_projecoes: false,
              contas_selecionadas: [],
              categorias_selecionadas: []
            })}
            className="text-xs h-6 px-2"
          >
            Limpar filtros
          </Button>
        </div>
      )}
    </div>
  );
}